import React, { useEffect, Component, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/lib/i18n";
import { Navbar, Footer } from "@/components/SiteChrome";
import Home from "@/pages/Home";
import { LegalNotice, Privacy, Terms } from "@/pages/Legal";
import "@/App.css";
import "@/components/visuals/visuals.css";

const Platform = lazy(() => import('@/pages/PlatformPage'));
const Freight = lazy(() => import('@/pages/FreightPage'));
const FinancialInstitutions = lazy(() => import('@/pages/InstitutionsPage'));
const Technology = lazy(() => import('@/pages/TechnologyPage'));
const Insights = lazy(() => import('@/pages/EditorialPage'));
const InsightArticle = lazy(() => import('@/pages/EditorialArticlePage'));
const About = lazy(() => import('@/pages/CompanyPage'));
const Contact = lazy(() => import('@/pages/ConversationPage'));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-paper">
          <p className="font-display text-lg font-semibold text-navy">
            N3LEDGE — something went wrong. Please refresh the page.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    let observer;
    const scrollToTarget = () => {
      const element = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (element) { element.scrollIntoView({ behavior: 'auto' }); observer?.disconnect(); return true; }
      return false;
    };
    if (!scrollToTarget()) {
      observer = new MutationObserver(scrollToTarget);
      observer.observe(document.getElementById('root'), { childList: true, subtree: true });
    }
    return () => observer?.disconnect();
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <ScrollManager />
        <ErrorBoundary>
          <Navbar />
          <Suspense fallback={<main id="main-content" className="wrap py-24" role="status" data-testid="page-loading">N3LEDGE — Loading…</main>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/freight" element={<Freight />} />
            <Route path="/financial-institutions" element={<FinancialInstitutions />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal" element={<LegalNotice />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Footer />
        </ErrorBoundary>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
      </MotionConfig>
    </LanguageProvider>
  );
}

export default App;
